import React, { useEffect, useState, useCallback } from "react";
import { AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getImpContext, clearImpersonation } from "@/lib/impersonation";

/**
 * Module 6D Phase 3 — Persistent impersonation banner.
 *
 * Mounted globally at the top of the protected app layout. Only renders
 * when sessionStorage carries an active imp_token (see lib/impersonation.js).
 *
 * Copy: "You are viewing [Studio] as support — read only" with a Stop button.
 * Also shows a live remaining-time countdown sourced from the impersonation
 * row's `expires_at` (30-min cap, never extended).
 *
 * Visual: amber/warning band, sticky to viewport top. Distinct from any
 * normal studio chrome so the support user can NEVER forget they're in
 * impersonation mode.
 *
 * Stop flow: POST /api/admin/impersonate/stop with Bearer imp_token
 * (the api.js interceptor handles header automatically), then
 * clearImpersonation() + close-or-redirect.
 */
export default function ImpersonationBanner() {
  const [ctx, setCtx] = useState(() => getImpContext());
  const [stopping, setStopping] = useState(false);
  const [remaining, setRemaining] = useState("");

  // Recompute remaining time every second.
  useEffect(() => {
    if (!ctx?.expires_at) {
      setRemaining("");
      return undefined;
    }
    const tick = () => {
      const exp = Date.parse(ctx.expires_at);
      if (Number.isNaN(exp)) { setRemaining(""); return; }
      const ms = exp - Date.now();
      if (ms <= 0) {
        setRemaining("expired");
        // Server-side TTL already invalidated; clear local state on next 401.
        return;
      }
      const totalSec = Math.floor(ms / 1000);
      const m = Math.floor(totalSec / 60);
      const s = String(totalSec % 60).padStart(2, "0");
      setRemaining(`${m}:${s}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [ctx?.expires_at]);

  const handleStop = useCallback(async () => {
    if (stopping) return;
    setStopping(true);
    try {
      await api.post("/admin/impersonate/stop");
      toast.success("Support session ended.");
    } catch (err) {
      // Even on error the server-side row may already be dead; clear local
      // state regardless so the banner doesn't lock the tab.
      toast.error("Could not stop cleanly — clearing local session.");
    } finally {
      clearImpersonation();
      setCtx(null);
      // The admin's `/admin` session lives in another tab; here we just
      // navigate to a "session ended" placeholder. The simplest correct
      // landing is the studio login page (this tab no longer has any
      // valid session).
      setTimeout(() => { window.location.replace("/"); }, 500);
    }
  }, [stopping]);

  if (!ctx?.token) return null;
  const label = ctx.studio_label || ctx.studio_id || "studio";

  return (
    <div
      data-testid="impersonation-banner"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 60,
        width: "100%",
        backgroundColor: "#7c2d12",
        color: "#fff7ed",
        borderBottom: "3px solid #fbbf24",
        boxShadow: "0 2px 12px rgba(0,0,0,0.25)",
      }}
    >
      <div
        className="flex items-center justify-between gap-4 px-4 py-2.5"
        style={{ maxWidth: "1500px", margin: "0 auto" }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <AlertTriangle size={18} style={{ color: "#fbbf24", flexShrink: 0 }} />
          <div className="min-w-0">
            <div
              className="text-sm font-semibold leading-tight truncate"
              data-testid="impersonation-banner-label"
            >
              You are viewing <span style={{ color: "#fbbf24" }}>{label}</span> as support — read only
            </div>
            <div
              className="text-xs leading-tight opacity-80 truncate"
              data-testid="impersonation-banner-context"
            >
              {ctx.admin_email ? `Acting as ${ctx.admin_email}` : "Support session"}
              {remaining ? <> · expires in <span data-testid="impersonation-banner-remaining">{remaining}</span></> : null}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={handleStop}
          disabled={stopping}
          data-testid="impersonation-banner-stop-button"
          className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase"
          style={{
            padding: "6px 12px",
            backgroundColor: "#fbbf24",
            color: "#7c2d12",
            border: "1px solid #f59e0b",
            borderRadius: 0,
            cursor: stopping ? "wait" : "pointer",
            opacity: stopping ? 0.7 : 1,
          }}
        >
          <X size={14} />
          {stopping ? "Stopping…" : "Stop"}
        </button>
      </div>
    </div>
  );
}
